<template>
  <div class="filter-panel-wrapper" v-click-outside="closePanel">
    <!-- ── Trigger button ─────────────────────────────────────── -->
    <i v-b-popover.hover.top="'Filters'">
      <el-badge :value="activeFiltersCount">
        <el-button class="container-filter mr-2" @click="togglePanel">
          <img
            class="container-filter__icons"
            src="@/assets/scss/icons/filter.svg"
            alt="filter-icon"
          />
        </el-button>
      </el-badge>
    </i>

    <!-- ── Floating panel ────────────────────────────────────── -->
    <div v-if="activeMenu" class="container-filter__options">
      <div class="filters-options">

        <!-- ── Left card: text inputs stacked vertically ──────── -->
        <div class="filters-options__card background-overlay">
          <!-- Name -->
          <div class="filter-field">
            <span class="filter-field__label" :class="{ 'filter-field__label--active': filters.name }">
              <b-icon icon="person" font-scale="1" />
              Name
            </span>
            <div class="input-container">
              <input type="text" class="input-information" v-model="filters.name" />
            </div>
          </div>

          <!-- Last Name -->
          <div class="filter-field">
            <span class="filter-field__label" :class="{ 'filter-field__label--active': filters.lastName }">
              <b-icon icon="person-badge" font-scale="1" />
              Last Name
            </span>
            <div class="input-container">
              <input type="text" class="input-information" v-model="filters.lastName" />
            </div>
          </div>

          <!-- Email -->
          <div class="filter-field">
            <span class="filter-field__label" :class="{ 'filter-field__label--active': filters.email }">
              <i class="ion ion-mail-outline"></i>
              Email
            </span>
            <div class="input-container">
              <input type="text" class="input-information" v-model="filters.email" />
            </div>
          </div>

          <!-- Account Name -->
          <div class="filter-field">
            <span class="filter-field__label" :class="{ 'filter-field__label--active': filters.accountName }">
              <i class="ion ion-business-outline"></i>
              Account Name
            </span>
            <el-select
              v-model="filters.accountName"
              placeholder="Select account"
              clearable
              filterable
            >
              <el-option
                v-for="opt in accountOptions"
                :key="opt.id"
                :label="opt.name"
                :value="opt.id"
              />
            </el-select>
          </div>
        </div>

        <!-- ── Right card: status toggle + selects ────────────── -->
        <div class="filters-options__card background-overlay">
          <!-- Status pill toggle -->
          <div class="filter-field">
            <span class="filter-field__label" :class="{ 'filter-field__label--active': filters.status && filters.status !== 'all' }">
              <i class="ion ion-checkmark-circle-outline"></i>
              Status
            </span>
            <ToggleSelector name="status" :index="0" v-model="statusToggle" />
          </div>

          <!-- Role -->
          <div class="filter-field">
            <span class="filter-field__label" :class="{ 'filter-field__label--active': filters.role }">
              <b-icon icon="person-badge" font-scale="1" />
              Role
            </span>
            <el-select v-model="filters.role" placeholder="Select role" clearable>
              <el-option label="GLT Admin" :value="1" />
              <el-option label="Account Admin" :value="2" />
              <el-option label="Sales Agent" :value="3" />
              <el-option label="Customer Agent" :value="4" />
              <el-option label="Cargo Care Admin" :value="2001" />
            </el-select>
          </div>

          <!-- Last Connection -->
          <div class="filter-field">
            <span class="filter-field__label" :class="{ 'filter-field__label--active': filters.lastConnection }">
              <b-icon icon="clock" font-scale="1" />
              Last Connection
            </span>
            <el-select v-model="filters.lastConnection" placeholder="Select period" clearable>
              <el-option label="Last 3 months" value="3" />
              <el-option label="Last 6 months" value="6" />
              <el-option label="Last 12 months" value="12" />
            </el-select>
          </div>
        </div>

      </div>

      <!-- Footer -->
      <div class="filters-footer">
        <button class="filters-footer__buttons" @click="onApply">Apply</button>
        <button class="filters-footer__buttons" @click="onReset">Reset</button>
        <button class="filters-footer__buttons" @click="closePanel">Close</button>
      </div>
    </div>
  </div>
</template>

<script>
import ToggleSelector from "../../AccountManagement/components/ToggleSelector.vue";

export default {
  name: "UsersFiltersPanel",
  components: { ToggleSelector },
  directives: {
    "click-outside": {
      bind(el, binding, vnode) {
        el.clickOutsideEvent = (event) => {
          if (!(el === event.target || el.contains(event.target))) {
            vnode.context[binding.expression](event);
          }
        };
        document.body.addEventListener("click", el.clickOutsideEvent);
      },
      unbind(el) {
        document.body.removeEventListener("click", el.clickOutsideEvent);
      },
    },
  },
  props: {
    filters: {
      type: Object,
      required: true,
    },
    accountOptions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      activeMenu: false,
    };
  },
  computed: {
    statusToggle: {
      get() {
        const map = {
          active: "on",
          all: "n/a",
          "": "n/a",
          inactive: "off",
        };
        return map[this.filters.status] ?? "n/a";
      },
      set(val) {
        const map = {
          on: "active",
          "n/a": "all",
          off: "inactive",
        };
        this.filters.status = map[val] ?? "all";
      },
    },
    activeFiltersCount() {
      return Object.values(this.filters).filter(
        (v) => v !== "" && v !== null && v !== undefined && v !== "all"
      ).length;
    },
  },
  methods: {
    togglePanel() {
      this.activeMenu = !this.activeMenu;
    },
    closePanel() {
      this.activeMenu = false;
    },
    onApply() {
      this.$emit("apply");
      this.activeMenu = false;
    },
    onReset() {
      this.$emit("reset");
    },
  },
};
</script>

<style lang="scss" scoped>
@use "@/assets/scss/_variables" as *;
@use "@/assets/scss/inputs/_inputs" as *;

// ── Wrapper — anchor for the floating dropdown ────────────────
.filter-panel-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
}

// ── Trigger button ────────────────────────────────────────────
.container-filter {
  padding: 0.4rem 0.4rem;
  background-color: #efefef;
  color: var(--color-primary-company);
  border: 2px solid var(--color-border-container);
  font-weight: 600;
  font-size: 13px;

  &:focus {
    background-color: $color-border-container;
    color: #fff;
  }

  &__icons {
    width: 18px;
    height: 18px;
  }

  // ── Floating dropdown ───────────────────────────────────────
  &__options {
    border: 2px solid var(--color-border-container);
    border-radius: 5px;
    padding: 5px;
    width: 500px;
    height: auto;
    background: #fff;
    box-shadow: 0 8px 24px rgba(13, 27, 62, 0.12);
    position: absolute;
    top: 100%;
    right: 0;
    margin-top: 6px;
    z-index: 50;
    font-family: $font-family-portal;
    overflow: visible;
  }
}

// ── Two-column layout ─────────────────────────────────────────
.filters-options {
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: 12px;
  align-items: start;

  &__card {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
  }
}

// ── Individual field wrapper ──────────────────────────────────
.filter-field {
  display: flex;
  flex-direction: column;
  gap: 3px;
  width: 100%;

  &__label {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 11px;
    font-family: $font-family-portal;
    color: #606266;
    transition: color 0.18s ease;

    i, svg {
      color: #6b7a99;
      font-size: 12px;
      transition: color 0.18s ease;
    }

    &--active {
      color: $color-border-container;

      i, svg {
        color: $color-border-container;
      }
    }
  }
}

// ── Text input ────────────────────────────────────────────────
.input-container {
  @include input-information;
  border: 1px solid #e4e7ed;
  color: $color-primary-company;
  padding: 5px 8px;
  width: 100%;
  box-sizing: border-box;
  height: 28px;
  box-shadow: none !important;
  border-radius: 5px;
  font-family: $font-family-portal;
  margin: 0px;

  &:hover {
    border-color: $color-border-container;
    box-shadow: none !important;
  }

  &:focus-within {
    border-color: $color-border-container;
    box-shadow: none !important;
  }
}

.input-information {
  @include input-password;
  width: 100%;
  font-size: 11px;
  color: #333333;
  background: transparent;
  border: none !important;
  outline: none !important;
  box-shadow: none !important;

  &:hover,
  &:focus {
    outline: none !important;
    box-shadow: none !important;
    border: none !important;
  }
}

// ── Status pill toggle ────────────────────────────────────────
.status-toggle {
  display: inline-flex;
  align-items: center;
  background-color: #e8edf5;
  border-radius: 20px;
  padding: 3px;
  gap: 2px;

  &__btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    border: none;
    background: transparent;
    color: #6b7a99;
    font-size: 14px;
    cursor: pointer;
    transition: background-color 0.18s ease, color 0.18s ease;

    &:hover {
      background-color: rgba(0, 185, 204, 0.15);
      color: $color-border-container;
    }

    &--active {
      background-color: $color-border-container;
      color: #ffffff;

      &:hover {
        background-color: $color-border-container;
        color: #ffffff;
      }
    }
  }
}

// ── Selects ───────────────────────────────────────────────────
::v-deep .el-select {
  width: 100%;

  .el-input__inner {
    border-radius: 5px;
    border: 1px solid #e4e7ed;
    color: #333333;
    height: 28px;
    font-family: $font-family-portal;
    font-size: 11px;
    padding: 0 30px 0 8px;

    &:hover {
      border-color: $color-border-container;
    }
  }

  .el-input__suffix {
    right: 4px;
  }
}

// ── Footer ────────────────────────────────────────────────────
.filters-footer {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 14px;
  gap: 8px;

  &__buttons {
    border-radius: 15px;
    border: 1px solid $color-border-container;
    background-color: #fff;
    color: $color-border-container;
    font-family: $font-family-portal;
    font-weight: 700;
    height: 26px;
    width: 72px;
    font-size: 11px;
    cursor: pointer;
    transition: background-color 0.18s ease, color 0.18s ease;

    &:hover,
    &:focus-within {
      background-color: $color-border-container;
      color: #fff;
    }
  }
}

// ── Background card ───────────────────────────────────────────
.background-overlay {
  background: #efefef;
  border-radius: 5px;
  padding: 6px;
}

// ── Badge ─────────────────────────────────────────────────────
::v-deep .el-badge__content {
  background-color: $color-border-container;
  font-size: 11px;
}

::v-deep .el-badge__content.is-fixed {
  top: 5px;
  right: 22px;
  z-index: 10;
}

::v-deep .el-button:focus,
::v-deep .el-button:hover {
  color: $color-border-container;
  border-color: $color-border-container;
  background-color: #00b9cc0f;
}

::v-deep  .el-input__icon {
    height: 100%;
    width: 25px;
    height: 34px;
    text-align: center;
    transition: all .3s;
    line-height: 40px;
}

</style>
